import { ChangeDetectorRef, Component, computed, input, ViewChildren } from '@angular/core';
import { FormArray } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { faCodeBranch, faGripDotsVertical, faMinus, faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { QuestionOption, RatingType } from '@empathy/survey/base';

import { QuestionOptionForm } from '../../../models/type-forms/question-option-form.model';
import { SurveyConditionalsService } from '../../../services/survey-conditionals.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { TextEditorComponent } from '../../text-editor/text-editor.component';
import { GetNextId } from '@eiq/shared/utils';

@Component({
  selector: 'empathy-rating-values-form',
  templateUrl: 'rating-values-form.component.html',
  styleUrls: ['./rating-values-form.component.scss', './_rating-values-form-theme.component.scss']
})
export class RatingValuesFormComponent {
  @ViewChildren(TextEditorComponent) ckEditor: TextEditorComponent[];

  isRankSort = input(false);
  maxValue = input<number>(this.isRankSort() ? null : 10);

  optionPrefix$ = computed(() => (this.isRankSort() ? 'rank_' : 'opt_'));
  minRatingScale$ = computed(() => (this.isRankSort() ? 1 : 2));

  faCheckCircle = faCheckCircle;
  faCodeBranch = faCodeBranch;
  faGripDotsVertical = faGripDotsVertical;
  faMinus = faMinus;
  faPlus = faPlus;
  faTrashCan = faTrashCan;

  justAddedOptionId: string;
  ratingType = RatingType;

  constructor(
    public surveyEditorService: SurveyEditorService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private surveyConditionalsService: SurveyConditionalsService
  ) {}

  get options(): FormArray {
    return this.isRankSort() ? this.surveyEditorService.surveyEditor$().activeQuestionRanks : this.surveyEditorService.surveyEditor$().activeQuestionOptions;
  }

  get addOptionAllowed(): boolean {
    return this.options.controls.length !== 0 && this.options.controls[this.options.controls.length - 1].value.text?.length > 0;
  }

  onAddOption(isLastOption: boolean, index: number = null): void {
    if (!!this.maxValue() && this.options.length >= this.maxValue()) {
      return;
    }

    if (!isLastOption) {
      const option = this.options.value[index + 1];
      const editor: DecoupledEditor = this.ckEditor.find((ed) => ed.id === option.id).editorInstance;
      editor.editing.view.focus();
      return;
    }

    if (!this.addOptionAllowed) {
      return;
    }

    if (this.options.controls.length === 0 || this.options.controls[this.options.controls.length - 1].value.text?.length > 0) {
      console.log('this.options', this.options.length, this.options.value);
      const newOptionId = GetNextId(this.options.value, this.optionPrefix$().slice(0, -1));
      const newOption = new QuestionOptionForm();
      newOption.form.get('id').setValue(newOptionId.id);
      newOption.form.get('value').setValue(newOptionId.value);
      this.justAddedOptionId = newOptionId.id;
      this.options.push(newOption.form);

      this.cdr.detectChanges();
    }
  }

  onRemoveAt(index: number, optionId: string): void {
    const affectedQuestionConditions = this.surveyConditionalsService.affectedQuestionConditionsByOptionRemoved(optionId);
    const affectedHiddenQuestionConditions = this.surveyConditionalsService.affectedHiddenQuestionConditionsByOptionRemoved(optionId);

    if (affectedQuestionConditions.length === 0 && affectedHiddenQuestionConditions.length === 0) {
      this.options.removeAt(index);
      return;
    }

    const affectedConditions = affectedQuestionConditions.concat(affectedHiddenQuestionConditions);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Option',
        confirmText: `The option removed is used as a conditional setting on <strong>${affectedConditions.join(', ')}</strong>. If continue the condition/s will be removed.`,
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.surveyEditorService.removeOptionAt(index, optionId);
      }
    });
  }

  onChangeRatingType(ratingType: RatingType): void {
    if (this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingType').value === ratingType) {
      return;
    }

    this.confirmChangeRatingType(ratingType);
  }

  onRoundNumber(): void {
    let currentScale = this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').value;
    currentScale = Math.ceil(currentScale);
    this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').setValue(currentScale);
  }

  onIncreaseScale(): void {
    let currentScale = this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').value;
    currentScale++;
    this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').setValue(currentScale);
  }

  onDecreaseScale(): void {
    let currentScale = this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').value;
    currentScale--;
    this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').setValue(currentScale);
  }

  onPaste(event: string[], pasteIndex: number): void {
    const currentOption = this.options.at(pasteIndex);
    const isCurrentEmpty = !currentOption.get('text').value;

    event.forEach((text, index) => {
      if (index === 0 && isCurrentEmpty) {
        currentOption.get('text').setValue(text);
      } else {
        const insertIndex = pasteIndex + index + (isCurrentEmpty ? 0 : 1);
        const { id, value } = GetNextId(this.options.value, this.optionPrefix$().slice(0, -1));
        const newOption = new QuestionOptionForm({ id, value, text } as QuestionOption);
        this.options.insert(insertIndex, newOption.form);
      }
    });

    this.updateOptionIds();
    this.cdr.detectChanges();
  }

  private updateOptionIds(): void {
    const prefix = this.optionPrefix$().slice(0, -1);
    this.options.controls.forEach((control, index) => {
      const newValue = `${index + 1}`;
      const newId = `${prefix}_${newValue}`;
      control.get('id').setValue(newId, { emitEvent: false });
      control.get('value').setValue(newValue, { emitEvent: false });
    });
  }

  onRatingInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = parseFloat(inputElement.value);

    if (isNaN(value)) {
      this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').setValue(0);
      inputElement.value = this.minRatingScale$().toString();
      return;
    }

    if (value < this.minRatingScale$()) {
      this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').setValue(this.minRatingScale$());
      inputElement.value = this.minRatingScale$().toString();
      return;
    }

    if (value > this.maxValue()) {
      this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').setValue(this.maxValue());
      inputElement.value = this.maxValue().toString();
      return;
    }

    this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingScale').setValue(value);

    this.onRoundNumber();
  }

  private confirmChangeRatingType(ratingType: RatingType): void {
    let confirmMessage: string;
    const affectedQuestions = this.surveyEditorService.surveyEditor$().affectedQuestionsOnConditional;
    if (affectedQuestions.length > 0) {
      confirmMessage = `This question is part of a condition on <strong>${affectedQuestions.join(', ')}</strong>. Are you sure you want to change the type?`;
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: { title: 'Confirm', confirmText: confirmMessage, confirmBtn: 'Remove', color: 'warn' }
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.switchRatingType(ratingType);
          this.surveyConditionalsService.removeAffectedConditions();
        }
      });
    } else {
      this.switchRatingType(ratingType);
    }
  }

  private switchRatingType(ratingType: RatingType): void {
    this.surveyEditorService.surveyEditor$().activeQuestion.get('ratingType').setValue(ratingType);
    if (ratingType === RatingType.numeric || ratingType === RatingType.stars) {
      this.options.clear();
    } else {
      if (this.options.length === 0) {
        this.options.push(new QuestionOptionForm({ id: `${this.optionPrefix$()}1`, value: '1' } as QuestionOption).form);
      }
    }
  }
}
